(() => {
    'use strict';

    /*
     * =========================================================
     * BOOST SD PRODUCT RECOMMENDATIONS
     * =========================================================
     *
     * MOBILE / IPHONE
     * ----------------
     * - Native Safari horizontal scrolling
     * - NO custom touchmove
     * - NO custom scrollLeft during touch
     * - NO preventDefault() for touch
     * - Keep iOS momentum scrolling
     * - Product image can start swipe
     * - Product image tap navigates to product URL
     *
     * DESKTOP
     * --------
     * - Custom mouse drag
     * - Horizontal drag only
     * - Vertical page scrolling remains normal
     * - Prevent accidental click after dragging
     *
     * BOOST SD
     * --------
     * - Supports async rendering
     * - Supports Slick content replacement
     * - Supports mobile nested Splide
     * - Re-applies image/CSS optimizations
     *
     * IMPORTANT
     * ---------
     * Touch is NOT implemented with touchmove.
     *
     * On mobile:
     * - touchstart is intercepted only to stop
     *   Slick/Splide from starting its own drag state.
     *
     * - tap detection uses touchstart + touchend coordinates.
     *
     * - Browser remains responsible for native scrolling.
     * =========================================================
     */


    /* =========================================================
       CONFIG
       ========================================================= */

    const SELECTOR = [
        '.related-products-section .swiper',
        '.related-section .boost-sd__slider .slick-list',
        '.recently-section .boost-sd__slider .slick-list'
    ].join(',');


    const DRAG_THRESHOLD = 6;

    const CLICK_BLOCK_FRAMES = 2;

    /*
     * Mobile tap detection.
     *
     * We intentionally do NOT use touchmove.
     *
     * We compare touchstart / touchend coordinates.
     */
    const MOBILE_TAP_DISTANCE = 10;

    const MOBILE_TAP_MAX_DURATION = 500;


    /* =========================================================
       HELPERS
       ========================================================= */

    const hasOverflow = slider => {
        return (
            slider.scrollWidth >
            slider.clientWidth + 1
        );
    };


    const isTouchDevice = () => {
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0
        );
    };


    /*
     * Swiper is intentionally untouched.
     */
    const isSwiperSlider = slider => {
        return false;
    };


    /* =========================================================
       FIND IMAGE AREA BY COORDINATES
       =========================================================
     *
     * We DO NOT rely on event.target.closest().
     *
     * Boost SD currently uses:
     *
     *   .boost-sd__product-image.splide {
     *       pointer-events: none;
     *   }
     *
     * and:
     *
     *   img {
     *       pointer-events: none;
     *   }
     *
     * Therefore the touch target may NOT be inside
     * .boost-sd__product-item-grid-view-layout-image.
     *
     * Instead we check the actual screen coordinates.
     */

    const getImageAreaAtPoint = (
        clientX,
        clientY
    ) => {

        const imageAreas =
            document.querySelectorAll(
                '.boost-sd__product-item-grid-view-layout-image'
            );


        for (
            let i = 0;
            i < imageAreas.length;
            i++
        ) {

            const imageArea =
                imageAreas[i];


            const rect =
                imageArea.getBoundingClientRect();


            if (
                clientX >= rect.left &&
                clientX <= rect.right &&
                clientY >= rect.top &&
                clientY <= rect.bottom
            ) {
                return imageArea;
            }
        }


        return null;
    };


    /* =========================================================
       MOBILE IMAGE TAP -> PRODUCT LINK
       =========================================================
     *
     * Flow:
     *
     * touchstart
     *     ↓
     * save coordinates
     *
     * touchend
     *     ↓
     * compare coordinates
     *
     * small movement = TAP
     * large movement = SWIPE
     *
     * TAP
     *     ↓
     * find image area by coordinates
     *     ↓
     * find product image <a>
     *     ↓
     * get href
     *     ↓
     * navigate
     *
     * No touchmove.
     * No preventDefault().
     * No scrollLeft.
     */

    const enableMobileImageTapNavigation = () => {

        if (!isTouchDevice()) {
            return;
        }


        /*
         * Only install this global listener once.
         */
        if (
            document.body.dataset
                .boostMobileImageTapReady ===
            'true'
        ) {
            return;
        }


        document.body.dataset
            .boostMobileImageTapReady =
            'true';


        let touchStartX = 0;

        let touchStartY = 0;

        let touchStartTime = 0;

        let touchStartedInsideImage = false;


        /* =====================================================
           TOUCH START
           ===================================================== */

        document.addEventListener(
            'touchstart',
            event => {

                const touch =
                    event.touches &&
                    event.touches[0];


                if (!touch) {
                    return;
                }


                touchStartX =
                    touch.clientX;


                touchStartY =
                    touch.clientY;


                touchStartTime =
                    Date.now();


                /*
                 * IMPORTANT:
                 *
                 * Do not use event.target.closest().
                 *
                 * Check coordinates instead.
                 */
                const imageArea =
                    getImageAreaAtPoint(
                        touch.clientX,
                        touch.clientY
                    );


                touchStartedInsideImage =
                    !!imageArea;
            },
            {
                capture: true,
                passive: true
            }
        );


        /* =====================================================
           TOUCH END
           ===================================================== */

        document.addEventListener(
            'touchend',
            event => {

                if (
                    !touchStartedInsideImage
                ) {
                    return;
                }


                const touch =
                    event.changedTouches &&
                    event.changedTouches[0];


                if (!touch) {
                    touchStartedInsideImage =
                        false;

                    return;
                }


                const deltaX =
                    Math.abs(
                        touch.clientX -
                        touchStartX
                    );


                const deltaY =
                    Math.abs(
                        touch.clientY -
                        touchStartY
                    );


                const duration =
                    Date.now() -
                    touchStartTime;


                /*
                 * Reset immediately.
                 */
                touchStartedInsideImage =
                    false;


                /*
                 * -------------------------------------------------
                 * SWIPE
                 * -------------------------------------------------
                 *
                 * If the finger moved enough, this is a swipe.
                 *
                 * DO NOT navigate.
                 */
                if (
                    deltaX >
                        MOBILE_TAP_DISTANCE ||
                    deltaY >
                        MOBILE_TAP_DISTANCE
                ) {
                    return;
                }


                /*
                 * Long press is not considered a tap.
                 */
                if (
                    duration >
                    MOBILE_TAP_MAX_DURATION
                ) {
                    return;
                }


                /*
                 * Find the image area again at the
                 * touch-end coordinate.
                 *
                 * This prevents navigating if the finger
                 * moved outside the product image.
                 */
                const imageArea =
                    getImageAreaAtPoint(
                        touch.clientX,
                        touch.clientY
                    );


                if (!imageArea) {
                    return;
                }


                /*
                 * Find the EXACT product image link.
                 */
                const imageLink =
                    imageArea.querySelector(
                        'a.boost-sd__product-link.boost-sd__product-link-image'
                    );


                if (!imageLink) {
                    console.log(
                        '[BOOST MOBILE] Image area found, but product image link not found.',
                        imageArea
                    );

                    return;
                }


                /*
                 * Get product URL.
                 */
                const href =
                    imageLink.getAttribute(
                        'href'
                    );


                if (!href) {
                    console.log(
                        '[BOOST MOBILE] Product image link has no href.',
                        imageLink
                    );

                    return;
                }


                console.log(
                    '[BOOST MOBILE] PRODUCT IMAGE TAP',
                    {
                        imageArea:
                            imageArea,

                        imageLink:
                            imageLink,

                        href:
                            href
                    }
                );


                /*
                 * Navigate to product.
                 *
                 * We intentionally do not call
                 * event.preventDefault().
                 *
                 * This keeps native touch behavior intact.
                 */
                window.location.href =
                    href;
            },
            {
                capture: true,
                passive: true
            }
        );
    };


    /* =========================================================
       IMAGE DRAG PROTECTION
       ========================================================= */

    const preventImageDrag = slider => {

        slider
            .querySelectorAll(
                'img:not([data-scroll-drag-ready])'
            )
            .forEach(image => {

                image.dataset.scrollDragReady =
                    'true';


                image.draggable =
                    false;


                image.addEventListener(
                    'dragstart',
                    event => {
                        event.preventDefault();
                    },
                    {
                        passive: false
                    }
                );
            });
    };


    /* =========================================================
       MOBILE
       RELEASE SLICK / SPLIDE TOUCH HANDLING
       =========================================================
     *
     * IMPORTANT:
     *
     * We DO NOT use:
     *
     *   touchmove
     *   scrollLeft
     *   preventDefault()
     *
     * We only intercept touchstart in CAPTURE phase.
     *
     * This prevents Slick/Splide's own touch handlers from
     * receiving the gesture.
     *
     * Browser can then handle the gesture natively.
     */

    const enableMobileNativeTouch = slider => {

        if (!isTouchDevice()) {
            return;
        }


        /*
         * Swiper must remain untouched.
         */
        if (isSwiperSlider(slider)) {
            return;
        }


        /*
         * Avoid duplicate listener.
         */
        if (
            slider.dataset.mobileNativeTouchReady ===
            'true'
        ) {
            return;
        }


        slider.dataset.mobileNativeTouchReady =
            'true';


        /*
         * -----------------------------------------------------
         * IMPORTANT
         * -----------------------------------------------------
         *
         * We cannot use:
         *
         *     event.target.closest(...)
         *
         * because the image/Splide layer has
         * pointer-events:none.
         *
         * Instead we check coordinates.
         */
        slider.addEventListener(
            'touchstart',
            event => {

                const touch =
                    event.touches &&
                    event.touches[0];


                if (!touch) {
                    return;
                }


                const imageArea =
                    getImageAreaAtPoint(
                        touch.clientX,
                        touch.clientY
                    );


                if (!imageArea) {
                    return;
                }


                /*
                 * Stop Slick/Splide from receiving
                 * this touchstart.
                 *
                 * NO preventDefault().
                 */
                event.stopImmediatePropagation();
            },
            {
                capture: true,
                passive: true
            }
        );
    };


    /* =========================================================
       MOBILE NATIVE SCROLL OPTIMIZATION
       ========================================================= */

    const applyMobileOptimization = slider => {

        /*
         * -----------------------------------------------------
         * SWIPER
         * -----------------------------------------------------
         *
         * Do not modify Swiper.
         */
        if (isSwiperSlider(slider)) {

            slider.style.touchAction =
                'pan-y';

            return;
        }


        /*
         * -----------------------------------------------------
         * SLICK
         * -----------------------------------------------------
         */

        slider.style.touchAction =
            'pan-x pan-y';


        /*
         * Native horizontal scrolling.
         */
        slider.style.overflowX =
            'auto';


        slider.style.overflowY =
            'hidden';


        /*
         * iOS momentum scrolling.
         */
        slider.style.webkitOverflowScrolling =
            'touch';


        /*
         * Native scrolling.
         */
        slider.style.scrollBehavior =
            'auto';


        /*
         * Prevent text selection.
         */
        slider.style.webkitUserSelect =
            'none';


        slider.style.userSelect =
            'none';


        /*
         * Prevent long-press callout.
         */
        slider.style.webkitTouchCallout =
            'none';


        /*
         * Keep horizontal overscroll inside slider.
         */
        slider.style.overscrollBehaviorX =
            'contain';


        /*
         * Hide scrollbar.
         */
        slider.style.scrollbarWidth =
            'none';


        slider.style.msOverflowStyle =
            'none';


        /*
         * -----------------------------------------------------
         * SLICK TRACK
         * -----------------------------------------------------
         */

        const track =
            slider.querySelector(
                '.slick-track'
            );


        if (track) {

            track.style.transition =
                'none';


            track.style.transform =
                'none';
        }


        /*
         * -----------------------------------------------------
         * NESTED SPLIDE
         * -----------------------------------------------------
         */

        const splides =
            slider.querySelectorAll(
                '.boost-sd__product-image.splide'
            );


        splides.forEach(splide => {

            splide.style.pointerEvents =
                'none';


            splide.dataset.mobileNativePassive =
                'true';


            splide
                .querySelectorAll('img')
                .forEach(image => {

                    image.style.pointerEvents =
                        'none';


                    image.style.userSelect =
                        'none';


                    image.style.webkitUserSelect =
                        'none';


                    image.style.webkitUserDrag =
                        'none';


                    image.draggable =
                        false;
                });
        });
    };


    /* =========================================================
       DESKTOP CLICK BLOCK
       ========================================================= */

    const createClickBlocker = slider => {

        return () => {

            slider.dataset.dragged =
                'true';


            let frames =
                CLICK_BLOCK_FRAMES;


            const clear = () => {

                frames--;


                if (frames <= 0) {

                    delete slider.dataset.dragged;

                    return;
                }


                requestAnimationFrame(
                    clear
                );
            };


            requestAnimationFrame(
                clear
            );
        };
    };


    /* =========================================================
       INITIALIZE ONE SLIDER
       ========================================================= */

    const initSlider = slider => {

        /*
         * Prevent duplicate initialization.
         */
        if (
            slider.dataset.scrollDragInitialized ===
            'true'
        ) {
            return;
        }


        slider.dataset.scrollDragInitialized =
            'true';


        /* =====================================================
           MOBILE
           ===================================================== */

        if (isTouchDevice()) {

            /*
             * Mobile image tap navigation.
             *
             * This is global and safe to call repeatedly.
             */
            enableMobileImageTapNavigation();


            /*
             * Enable native mobile touch BEFORE
             * applying slider CSS.
             */
            enableMobileNativeTouch(
                slider
            );


            applyMobileOptimization(
                slider
            );
        }


        /* =====================================================
           CLICK PROTECTION
           ===================================================== */

        const blockNextClick =
            createClickBlocker(
                slider
            );


        /* =====================================================
           DESKTOP POINTER STATE
           ===================================================== */

        let pointerId = null;

        let pointerStartX = 0;

        let pointerStartY = 0;

        let pointerStartScrollLeft = 0;

        let pointerDown = false;

        let pointerDragging = false;


        /* =====================================================
           RELEASE POINTER
           ===================================================== */

        const releasePointer = () => {

            if (
                pointerId !== null &&
                slider.hasPointerCapture &&
                slider.hasPointerCapture(
                    pointerId
                )
            ) {

                try {

                    slider.releasePointerCapture(
                        pointerId
                    );

                } catch (_) {

                    /*
                     * Ignore browser-specific errors.
                     */
                }
            }


            pointerId = null;
        };


        /* =====================================================
           FINISH DESKTOP POINTER
           ===================================================== */

        const finishPointer = event => {

            if (
                pointerId !== null &&
                event &&
                event.pointerId !== pointerId
            ) {
                return;
            }


            if (!pointerDown) {
                return;
            }


            const wasDragging =
                pointerDragging;


            pointerDown = false;

            pointerDragging = false;


            slider.classList.remove(
                'is-dragging'
            );


            releasePointer();


            if (wasDragging) {

                blockNextClick();
            }
        };


        /* =====================================================
           DESKTOP POINTER DOWN
           ===================================================== */

        slider.addEventListener(
            'pointerdown',
            event => {

                /*
                 * NEVER process touch.
                 *
                 * Native browser handles mobile.
                 */
                if (
                    event.pointerType ===
                    'touch'
                ) {
                    return;
                }


                /*
                 * Only left mouse button.
                 */
                if (
                    event.pointerType ===
                        'mouse' &&
                    event.button !== 0
                ) {
                    return;
                }


                /*
                 * Nothing to drag.
                 */
                if (
                    !hasOverflow(slider)
                ) {
                    return;
                }


                pointerId =
                    event.pointerId;


                pointerStartX =
                    event.clientX;


                pointerStartY =
                    event.clientY;


                pointerStartScrollLeft =
                    slider.scrollLeft;


                pointerDown = true;

                pointerDragging = false;
            },
            {
                passive: true
            }
        );


        /* =====================================================
           DESKTOP POINTER MOVE
           ===================================================== */

        slider.addEventListener(
            'pointermove',
            event => {

                /*
                 * NEVER process touch.
                 */
                if (
                    event.pointerType ===
                    'touch'
                ) {
                    return;
                }


                if (
                    !pointerDown ||
                    event.pointerId !==
                    pointerId
                ) {
                    return;
                }


                const deltaX =
                    event.clientX -
                    pointerStartX;


                const deltaY =
                    event.clientY -
                    pointerStartY;


                /* ---------------------------------------------
                   WAIT FOR DRAG THRESHOLD
                   --------------------------------------------- */

                if (!pointerDragging) {

                    if (
                        Math.abs(deltaX) <
                            DRAG_THRESHOLD &&
                        Math.abs(deltaY) <
                            DRAG_THRESHOLD
                    ) {
                        return;
                    }


                    /*
                     * Vertical movement.
                     */
                    if (
                        Math.abs(deltaY) >
                        Math.abs(deltaX)
                    ) {

                        pointerDown = false;

                        pointerDragging =
                            false;

                        releasePointer();

                        return;
                    }


                    /*
                     * Horizontal mouse drag.
                     */
                    pointerDragging =
                        true;


                    slider.classList.add(
                        'is-dragging'
                    );


                    try {

                        slider.setPointerCapture(
                            pointerId
                        );

                    } catch (_) {

                        /*
                         * Ignore unsupported browsers.
                         */
                    }
                }


                /*
                 * Desktop only.
                 */
                event.preventDefault();


                /*
                 * Horizontal mouse drag.
                 */
                slider.scrollLeft =
                    pointerStartScrollLeft -
                    deltaX;
            },
            {
                passive: false
            }
        );


        /* =====================================================
           POINTER UP
           ===================================================== */

        slider.addEventListener(
            'pointerup',
            finishPointer,
            {
                passive: true
            }
        );


        /* =====================================================
           POINTER CANCEL
           ===================================================== */

        slider.addEventListener(
            'pointercancel',
            finishPointer,
            {
                passive: true
            }
        );


        /* =====================================================
           LOST POINTER CAPTURE
           ===================================================== */

        slider.addEventListener(
            'lostpointercapture',
            () => {

                /*
                 * Intentionally empty.
                 */
            }
        );


        /* =====================================================
           CLICK PROTECTION
           ===================================================== */

        slider.addEventListener(
            'click',
            event => {

                if (
                    slider.dataset.dragged !==
                    'true'
                ) {
                    return;
                }


                event.preventDefault();

                event.stopPropagation();
            },
            true
        );


        /* =====================================================
           NATIVE DRAG PROTECTION
           ===================================================== */

        slider.addEventListener(
            'dragstart',
            event => {

                event.preventDefault();

            },
            {
                passive: false
            }
        );


        /* =====================================================
           INITIAL IMAGES
           ===================================================== */

        preventImageDrag(
            slider
        );


        /* =====================================================
           BOOST SD CONTENT OBSERVER
           ===================================================== */

        let mutationScheduled =
            false;


        const observer =
            new MutationObserver(
                () => {

                    if (
                        mutationScheduled
                    ) {
                        return;
                    }


                    mutationScheduled =
                        true;


                    requestAnimationFrame(
                        () => {

                            mutationScheduled =
                                false;


                            /*
                             * New images/products.
                             */
                            preventImageDrag(
                                slider
                            );


                            /*
                             * Boost/Slick can replace
                             * the internal DOM.
                             */
                            if (
                                isTouchDevice()
                            ) {

                                /*
                                 * Re-apply mobile CSS.
                                 */
                                applyMobileOptimization(
                                    slider
                                );


                                /*
                                 * Touchstart listener remains
                                 * attached to slick-list.
                                 */
                                enableMobileNativeTouch(
                                    slider
                                );


                                /*
                                 * Global mobile tap listener
                                 * remains active.
                                 */
                                enableMobileImageTapNavigation();
                            }
                        }
                    );
                }
            );


        observer.observe(
            slider,
            {
                childList: true,
                subtree: true
            }
        );


        /* =====================================================
           WHEEL / TRACKPAD
           ===================================================== */

        slider.addEventListener(
            'wheel',
            event => {

                if (
                    !hasOverflow(slider)
                ) {
                    return;
                }


                /*
                 * Native horizontal trackpad gesture.
                 */
                if (
                    Math.abs(
                        event.deltaX
                    ) >=
                    Math.abs(
                        event.deltaY
                    )
                ) {
                    return;
                }


                /*
                 * Vertical wheel -> horizontal.
                 *
                 * This is desktop only behavior.
                 */
                if (!isTouchDevice()) {

                    slider.scrollLeft +=
                        event.deltaY;
                }
            },
            {
                passive: true
            }
        );
    };


    /* =========================================================
       INITIALIZE ALL SLIDERS
       ========================================================= */

    const initAll = () => {

        /*
         * Install mobile tap detection even if the
         * Boost slider has not rendered yet.
         */
        if (isTouchDevice()) {
            enableMobileImageTapNavigation();
        }


        document
            .querySelectorAll(
                SELECTOR
            )
            .forEach(
                initSlider
            );
    };


    /* =========================================================
       INITIAL LOAD
       ========================================================= */

    if (
        document.readyState ===
        'loading'
    ) {

        document.addEventListener(
            'DOMContentLoaded',
            initAll,
            {
                once: true
            }
        );

    } else {

        initAll();
    }


    /* =========================================================
       BOOST SD ASYNC RENDER
       ========================================================= */

    let pageMutationScheduled =
        false;


    const pageObserver =
        new MutationObserver(
            () => {

                if (
                    pageMutationScheduled
                ) {
                    return;
                }


                pageMutationScheduled =
                    true;


                requestAnimationFrame(
                    () => {

                        pageMutationScheduled =
                            false;


                        initAll();
                    }
                );
            }
        );


    pageObserver.observe(
        document.body,
        {
            childList: true,
            subtree: true
        }
    );

})();
